import { z } from "zod";
import { digitalKindSchema } from "./catalog-books.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { publicImageSchema, storefrontHostSchema } from "./storefront.js";
/**
 * DG-06 (docs/specs/digital.md): a variant's digital side (its content, e.g. the eBook PDF), and the library of what
 * each shopper account bought — granted when the order is paid, removed on refund or cancellation.
 */
export const digitalItemSchema = z.object({
    variantId: z.uuid(),
    kind: digitalKindSchema,
    /** `ready` once the kind's content is complete: only then can it be bought. */
    status: z.enum(["draft", "ready"]),
    ebook: z
        .object({
        fileId: z.uuid(),
        sampleFileId: z.uuid().nullable(),
        sampleStatus: z.enum(["none", "marking", "ready", "failed"]),
    })
        .nullable(),
});
export const libraryItemSchema = z.object({
    id: z.uuid(),
    accountId: z.uuid(),
    productId: z.uuid(),
    variantId: z.uuid(),
    productName: z.string(),
    kind: digitalKindSchema,
    orderNumber: z.string().nullable(),
    source: z.enum(["order", "manual"]),
    grantedAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    revokeReason: z.string().nullable(),
});
const catalogTag = { tag: "catalog" };
const tag = { tag: "digital" };
const reason = z.string().trim().min(1).max(200);
export const getDigitalItem = defineEndpoint({
    ...catalogTag,
    name: "getDigitalItem",
    method: "GET",
    path: "/v1/variants/{id}/digital",
    access: { permission: "catalog.view" },
    summary: "A digital variant's content and whether it can be sold",
    params: idParamsSchema,
    status: 200,
    response: digitalItemSchema,
    errors: ["NOT_FOUND"],
});
export const setEbookContent = defineEndpoint({
    ...catalogTag,
    name: "setEbookContent",
    method: "PUT",
    path: "/v1/variants/{id}/digital/ebook",
    access: { permission: "catalog.manage" },
    summary: "Attach the eBook PDF (and its sample) to a digital eBook variant; it can then be sold",
    params: idParamsSchema,
    body: z.object({ fileId: z.uuid(), sampleFileId: z.uuid().nullable().default(null) }).strict(),
    status: 200,
    response: digitalItemSchema,
    errors: ["NOT_FOUND", "BUSINESS_RULE_VIOLATION"],
});
export const listLibraryItems = defineEndpoint({
    ...tag,
    name: "listLibraryItems",
    method: "GET",
    path: "/v1/library-items",
    access: { permission: "shopper_accounts.view" },
    summary: "What a shopper account owns, or what an order granted (revoked ones included)",
    query: z.object({ accountId: z.uuid().optional(), orderId: z.uuid().optional() }),
    status: 200,
    response: z.object({ data: z.array(libraryItemSchema) }),
});
export const grantLibraryItem = defineEndpoint({
    ...tag,
    name: "grantLibraryItem",
    method: "POST",
    path: "/v1/library-items",
    access: { permission: "digital.manage" },
    idempotent: true,
    summary: "Give a shopper account a digital product by hand (a gift, a fix); audited",
    body: z.object({ accountId: z.uuid(), variantId: z.uuid(), reason }).strict(),
    status: 201,
    response: libraryItemSchema,
    errors: ["NOT_FOUND", "ALREADY_IN_LIBRARY", "VARIANT_NOT_SELLABLE"],
});
export const revokeLibraryItem = defineEndpoint({
    ...tag,
    name: "revokeLibraryItem",
    method: "POST",
    path: "/v1/library-items/{id}/revoke",
    access: { permission: "digital.manage" },
    idempotent: true,
    summary: "Take a digital product out of a shopper's library; audited",
    params: idParamsSchema,
    body: z.object({ reason }).strict(),
    status: 200,
    response: libraryItemSchema,
    errors: ["NOT_FOUND"],
});
// The shopper's library -------------------------------------------------------------------------------------------
export const shopLibraryItemSchema = z.object({
    id: z.uuid(),
    kind: digitalKindSchema,
    productName: z.string(),
    productSlug: z.string().nullable(),
    cover: publicImageSchema.nullable(),
    grantedAt: z.iso.datetime(),
    /** eBooks: the page the shopper reached and the page count (null until opened). */
    page: z.int().nullable(),
    pageCount: z.int().nullable(),
});
const shopperToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const visitorIp = z.string().max(64).default("");
export const shopLibrary = defineEndpoint({
    tag: "storefront",
    name: "shopLibrary",
    method: "POST",
    path: "/v1/storefront/library",
    access: { public: true },
    summary: "The signed-in shopper's digital products in this shop, newest first",
    body: z.object({ host: storefrontHostSchema, token: shopperToken }).strict(),
    status: 200,
    response: z.object({ data: z.array(shopLibraryItemSchema) }),
    errors: ["UNAUTHENTICATED"],
});
// The eBook reader (DG-07, rules 10–11) ---------------------------------------------------------------------------
/** A signed link to the PDF, valid 15 minutes; the reader asks for a new one when it runs out. Never logged. */
export const ebookLinkSchema = z.object({
    url: z.url(),
    expiresAt: z.iso.datetime(),
});
export const readingPositionSchema = z.object({
    /** The page to open at (1 for a book not read yet). */
    page: z.int().min(1),
    /** Known once the reader has opened the book. */
    pageCount: z.int().min(1).nullable(),
});
export const shopEbookOpenSchema = ebookLinkSchema.extend({
    title: z.string(),
    ...readingPositionSchema.shape,
});
export const openLibraryEbook = defineEndpoint({
    tag: "storefront",
    name: "openLibraryEbook",
    method: "POST",
    path: "/v1/storefront/library/{id}/ebook",
    access: { public: true },
    summary: "Open an eBook the signed-in shopper owns: a 15-minute signed link and the page they reached",
    params: idParamsSchema,
    body: z.object({ host: storefrontHostSchema, token: shopperToken, ip: visitorIp }).strict(),
    status: 200,
    response: shopEbookOpenSchema,
    errors: ["UNAUTHENTICATED", "NOT_FOUND"],
});
export const saveReadingProgress = defineEndpoint({
    tag: "storefront",
    name: "saveReadingProgress",
    method: "POST",
    path: "/v1/storefront/library/{id}/progress",
    access: { public: true },
    summary: "Remember the page the shopper reached in an eBook they own",
    params: idParamsSchema,
    body: z
        .object({
        host: storefrontHostSchema,
        token: shopperToken,
        page: z.int().min(1).max(100_000),
        pageCount: z.int().min(1).max(100_000),
    })
        .strict()
        .refine((b) => b.page <= b.pageCount, { path: ["page"], message: "page_after_last" }),
    status: 200,
    response: z.object({ page: z.int().min(1), pageCount: z.int().min(1) }),
    errors: ["UNAUTHENTICATED", "NOT_FOUND"],
});
export const shopSampleOpenSchema = ebookLinkSchema.extend({
    title: z.string(),
    /** The product page, where the shopper buys the eBook. */
    productSlug: z.string(),
});
export const openEbookSample = defineEndpoint({
    tag: "storefront",
    name: "openEbookSample",
    method: "POST",
    path: "/v1/storefront/ebooks/{id}/sample",
    access: { public: true },
    summary: "Read a little: a signed link to the watermarked sample of a published eBook (no sign-in)",
    params: idParamsSchema,
    body: z.object({ host: storefrontHostSchema, ip: visitorIp }).strict(),
    status: 200,
    response: shopSampleOpenSchema,
    errors: ["NOT_FOUND", "RATE_LIMITED"],
});
//# sourceMappingURL=digital.js.map